import ExecutiveDiagramFrame from './ExecutiveDiagramFrame';

const gates = [
  { id: 'broker', label: 'Broker Health' },
  { id: 'session', label: 'Market Session' },
  { id: 'data', label: 'Data Integrity' },
  { id: 'risk', label: 'Risk Budget' },
];

/**
 * Pre-trade readiness — every gate clears before capital moves, or the order holds.
 */
const PATiReadinessGateDiagram = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const bg = isDark ? '#060d18' : '#f0f9ff';
  const grid = isDark ? '#1e293b' : '#cbd5e1';
  const panel = isDark ? '#0f172a' : '#ffffff';
  const label = isDark ? '#f1f5f9' : '#0f172a';
  const muted = '#64748b';
  const teal = isDark ? '#2dd4bf' : '#0d9488';
  const sapphire = isDark ? '#60a5fa' : '#2563eb';
  const amber = isDark ? '#fbbf24' : '#d97706';
  const emerald = isDark ? '#34d399' : '#059669';

  return (
    <ExecutiveDiagramFrame
      theme={theme}
      caption="Pre-Trade Readiness · Every Gate Clears Or The Order Holds"
      className="pati-gate-frame"
    >
      <svg
        viewBox="0 0 720 380"
        className="w-full h-auto pati-gate-svg"
        role="img"
        aria-label="Pre-trade readiness gates: broker health, market session, data integrity, and risk budget must all clear before an order is released, otherwise it is held"
      >
        <defs>
          <filter
            id="patiGateGlow"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect width="720" height="380" fill={bg} rx="12" />

        {Array.from({ length: 13 }).map((_, i) => (
          <line
            key={`vg-${i}`}
            x1={60 + i * 50}
            y1="48"
            x2={60 + i * 50}
            y2="340"
            stroke={grid}
            strokeWidth="0.5"
            opacity="0.3"
          />
        ))}
        {Array.from({ length: 6 }).map((_, i) => (
          <line
            key={`hg-${i}`}
            x1="40"
            y1={60 + i * 48}
            x2="680"
            y2={60 + i * 48}
            stroke={grid}
            strokeWidth="0.5"
            opacity="0.3"
          />
        ))}

        <text
          x="360"
          y="30"
          textAnchor="middle"
          fill={muted}
          fontSize="10"
          letterSpacing="4"
          fontWeight="600"
        >
          PRE-TRADE READINESS
        </text>

        {/* Order intent */}
        <rect
          x="40"
          y="96"
          width="152"
          height="152"
          rx="10"
          fill={panel}
          stroke={grid}
          strokeWidth="1"
          opacity="0.95"
        />
        <text
          x="116"
          y="122"
          textAnchor="middle"
          fill={sapphire}
          fontSize="9"
          fontWeight="700"
          letterSpacing="2"
        >
          ORDER INTENT
        </text>
        <line
          x1="60"
          y1="132"
          x2="172"
          y2="132"
          stroke={grid}
          strokeWidth="1"
        />
        {['Sized', 'Session Scoped', 'Awaiting Release'].map((row, i) => (
          <g key={row}>
            <circle
              cx="66"
              cy={155 + i * 26}
              r="3"
              fill={sapphire}
              opacity="0.8"
            />
            <text x="78" y={159 + i * 26} fill={label} fontSize="9">
              {row}
            </text>
          </g>
        ))}
        <text x="116" y="236" textAnchor="middle" fill={muted} fontSize="8">
          Nothing Moves Yet
        </text>

        {/* Rail into the gates */}
        <line
          x1="192"
          y1="172"
          x2="228"
          y2="172"
          stroke={teal}
          strokeWidth="1.5"
          opacity="0.7"
        />
        <circle r="4" fill={teal} filter="url(#patiGateGlow)">
          <animateMotion
            dur="3s"
            repeatCount="indefinite"
            path="M 192 172 L 228 172"
          />
        </circle>

        {/* Gate stack */}
        <rect
          x="228"
          y="80"
          width="264"
          height="200"
          rx="10"
          fill={panel}
          stroke={teal}
          strokeWidth="1"
          opacity="0.95"
        />
        <text
          x="360"
          y="102"
          textAnchor="middle"
          fill={teal}
          fontSize="9"
          fontWeight="700"
          letterSpacing="2"
        >
          READINESS GATES
        </text>

        {gates.map(({ id, label: gateLabel }, i) => {
          const y = 116 + i * 38;
          const isHoldGate = id === 'data';
          return (
            <g key={id} className={`pati-gate-row pati-gate-row-${i}`}>
              <rect
                x="248"
                y={y}
                width="224"
                height="30"
                rx="5"
                fill="none"
                stroke={grid}
                strokeWidth="1"
                className={`pati-gate-box pati-gate-box-${i}`}
              />
              <text
                x="262"
                y={y + 19}
                fill={label}
                fontSize="9.5"
                fontWeight="600"
              >
                {gateLabel}
              </text>
              <circle
                cx="452"
                cy={y + 15}
                r="4"
                fill={isHoldGate ? amber : emerald}
                className={
                  isHoldGate
                    ? 'pati-gate-dot-hold'
                    : `pati-gate-dot pati-gate-dot-${i}`
                }
              />
              <text
                x="440"
                y={y + 19}
                textAnchor="end"
                fill={isHoldGate ? amber : emerald}
                fontSize="8"
                fontWeight="600"
                letterSpacing="1"
              >
                {isHoldGate ? 'CHECKING' : 'CLEAR'}
              </text>
            </g>
          );
        })}

        {/* Rails to outcomes */}
        <path
          d="M 492 150 L 512 150 L 512 134 L 524 134"
          fill="none"
          stroke={emerald}
          strokeWidth="1.5"
          opacity="0.7"
        />
        <path
          d="M 492 210 L 512 210 L 512 234 L 524 234"
          fill="none"
          stroke={amber}
          strokeWidth="1.5"
          opacity="0.5"
          strokeDasharray="4 3"
        />

        {/* Release */}
        <g className="pati-gate-release">
          <rect
            x="524"
            y="104"
            width="156"
            height="62"
            rx="10"
            fill={panel}
            stroke={emerald}
            strokeWidth="1.5"
            opacity="0.95"
          />
          <text
            x="602"
            y="128"
            textAnchor="middle"
            fill={emerald}
            fontSize="10"
            fontWeight="700"
            letterSpacing="2"
          >
            RELEASE
          </text>
          <text x="602" y="146" textAnchor="middle" fill={muted} fontSize="8">
            All Gates Clear
          </text>
          <circle
            cx="664"
            cy="118"
            r="4"
            fill={emerald}
            filter="url(#patiGateGlow)"
            className="pati-gate-release-dot"
          />
        </g>

        {/* Hold */}
        <g className="pati-gate-hold">
          <rect
            x="524"
            y="204"
            width="156"
            height="62"
            rx="10"
            fill={panel}
            stroke={amber}
            strokeWidth="1.5"
            opacity="0.95"
          />
          <text
            x="602"
            y="228"
            textAnchor="middle"
            fill={amber}
            fontSize="10"
            fontWeight="700"
            letterSpacing="2"
          >
            HOLD
          </text>
          <text x="602" y="246" textAnchor="middle" fill={muted} fontSize="8">
            Any Gate Unclear
          </text>
        </g>

        {/* Fail-closed statement */}
        <rect
          x="40"
          y="304"
          width="640"
          height="40"
          rx="10"
          fill={panel}
          stroke={grid}
          strokeWidth="1"
          opacity="0.9"
        />
        <circle
          cx="70"
          cy="324"
          r="5"
          fill={teal}
          filter="url(#patiGateGlow)"
          className="pati-gate-pulse"
        />
        <text
          x="90"
          y="328"
          fill={label}
          fontSize="10"
          fontWeight="600"
          letterSpacing="1"
        >
          Ambiguous State Does Not Trade
        </text>
        <text
          x="660"
          y="328"
          textAnchor="end"
          fill={muted}
          fontSize="8"
          letterSpacing="1"
        >
          Fail-Closed By Default
        </text>
      </svg>
    </ExecutiveDiagramFrame>
  );
};

export default PATiReadinessGateDiagram;
